import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { Lightning, ArrowsOut, Code, DownloadSimple, Eye, Globe, Sun, Moon, FunnelSimple, Image, Sparkle } from "@phosphor-icons/react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WebsiteGenerator() {
  const [searchParams] = useSearchParams();
  const [businessName, setBusinessName] = useState(searchParams.get("name") || "");
  const [industry, setIndustry] = useState(searchParams.get("industry") || "");
  const [keywords, setKeywords] = useState("");
  const [styleNotes, setStyleNotes] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [theme, setTheme] = useState("auto");
  const [funnelType, setFunnelType] = useState("conversion");
  const [bookingLink, setBookingLink] = useState("");
  const [generating, setGenerating] = useState(false);
  const [generatedHtml, setGeneratedHtml] = useState("");
  const [websiteId, setWebsiteId] = useState(null);
  const [showCode, setShowCode] = useState(false);
  const [enhanceUrl, setEnhanceUrl] = useState("");
  const [enhanceType, setEnhanceType] = useState("professional");
  const [enhancing, setEnhancing] = useState(false);
  const [enhancedImage, setEnhancedImage] = useState(null);
  const iframeRef = useRef(null);
  const leadId = searchParams.get("leadId") || null;

  // If we have a leadId, fetch lead data
  useEffect(() => {
    if (leadId) {
      axios.get(`${API}/leads/${leadId}`).then((res) => {
        const lead = res.data;
        if (lead.business_name && !businessName) setBusinessName(lead.business_name);
        if (lead.industry && !industry) setIndustry(lead.industry);
        if (lead.phones?.[0] && !phone) setPhone(lead.phones[0]);
        if (lead.emails?.[0] && !email) setEmail(lead.emails[0]);
      }).catch(console.error);
    }
  }, [leadId]);

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!businessName.trim() || !industry.trim() || !keywords.trim()) {
      toast.error("Fill in business name, industry, and keywords");
      return;
    }
    setGenerating(true);
    setGeneratedHtml("");
    setWebsiteId(null);
    try {
      // Start generation (returns immediately)
      const res = await axios.post(`${API}/websites/generate`, {
        business_name: businessName.trim(),
        industry: industry.trim(),
        keywords: keywords.trim(),
        style_notes: styleNotes.trim(),
        lead_id: leadId,
        phone: phone.trim(),
        email: email.trim(),
        theme,
        funnel_type: funnelType,
        booking_link: bookingLink.trim(),
      });
      const wId = res.data.id;
      setWebsiteId(wId);
      toast.info("Generating website... this takes 20-40 seconds");

      // Poll for completion
      const poll = async () => {
        for (let i = 0; i < 40; i++) {
          await new Promise((r) => setTimeout(r, 3000));
          try {
            const status = await axios.get(`${API}/websites/${wId}/status`);
            if (status.data.status === "ready") {
              const full = await axios.get(`${API}/websites/${wId}`);
              setGeneratedHtml(full.data.html_content || "");
              setGenerating(false);
              toast.success("Website generated!");
              return;
            }
            if (status.data.status === "failed") {
              setGenerating(false);
              toast.error("Generation failed. Try again.");
              return;
            }
          } catch (e) {
            console.error("Poll error:", e);
          }
        }
        setGenerating(false);
        toast.error("Generation timed out. Check Website Gallery.");
      };
      poll();
    } catch (e) {
      toast.error("Generation failed. Check your API key balance.");
      console.error(e);
      setGenerating(false);
    }
  };

  const handleDownload = () => {
    if (!generatedHtml) return;
    const blob = new Blob([generatedHtml], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${businessName.replace(/\s+/g, "_").toLowerCase() || "website"}.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFullscreen = () => {
    if (!websiteId) return;
    window.open(`${API}/websites/${websiteId}/html`, "_blank");
  };

  return (
    <div data-testid="website-generator-page" className="space-y-6">
      <div>
        <h1
          className="text-3xl sm:text-4xl font-black tracking-tighter"
          style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}
        >
          Site Generator
        </h1>
        <p className="text-sm text-[#52525B] mt-1">
          AI-powered website creation. Enter keywords, get a production-ready site in seconds.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" style={{ minHeight: "70vh" }}>
        {/* Controls Panel */}
        <div className="bg-white border border-border p-5 space-y-4">
          <h3 className="text-base font-bold tracking-tight" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
            <Lightning weight="bold" size={16} className="inline mr-2 text-[#002FA7]" />
            Configuration
          </h3>

          <form onSubmit={handleGenerate} className="space-y-3">
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#52525B] block mb-1">Business Name</label>
              <Input
                data-testid="gen-business-name"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                placeholder="Joe's Plumbing"
                className="border-border rounded-none focus:border-[#002FA7] focus:ring-0"
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#52525B] block mb-1">Industry</label>
              <Input
                data-testid="gen-industry"
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
                placeholder="Plumbing & Heating"
                className="border-border rounded-none focus:border-[#002FA7] focus:ring-0"
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#52525B] block mb-1">Keywords / Services</label>
              <Textarea
                data-testid="gen-keywords"
                value={keywords}
                onChange={(e) => setKeywords(e.target.value)}
                placeholder="Drain cleaning, water heater repair, emergency plumbing, pipe replacement"
                rows={3}
                className="border-border rounded-none focus:border-[#002FA7] focus:ring-0 resize-none"
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#52525B] block mb-1">Style Notes (optional)</label>
              <Input
                data-testid="gen-style-notes"
                value={styleNotes}
                onChange={(e) => setStyleNotes(e.target.value)}
                placeholder="Professional blue color scheme, modern"
                className="border-border rounded-none focus:border-[#002FA7] focus:ring-0"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#52525B] block mb-1">Phone</label>
                <Input
                  data-testid="gen-phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="(555) 123-4567"
                  className="border-border rounded-none focus:border-[#002FA7] focus:ring-0"
                />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#52525B] block mb-1">Email</label>
                <Input
                  data-testid="gen-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="joe@plumbing.com"
                  className="border-border rounded-none focus:border-[#002FA7] focus:ring-0"
                />
              </div>
            </div>

            {/* Theme & Funnel */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#52525B] block mb-1">
                  <Sun weight="bold" size={10} className="inline mr-1" />Theme
                </label>
                <Select value={theme} onValueChange={setTheme}>
                  <SelectTrigger data-testid="gen-theme" className="rounded-none border-border">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Auto (AI Picks)</SelectItem>
                    <SelectItem value="light">Light / Clean</SelectItem>
                    <SelectItem value="dark">Dark / Premium</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#52525B] block mb-1">
                  <FunnelSimple weight="bold" size={10} className="inline mr-1" />Funnel Type
                </label>
                <Select value={funnelType} onValueChange={setFunnelType}>
                  <SelectTrigger data-testid="gen-funnel" className="rounded-none border-border">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="conversion">Conversion Funnel</SelectItem>
                    <SelectItem value="long-form">Long-Form (15+ sections)</SelectItem>
                    <SelectItem value="simple">Simple (7 sections)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#52525B] block mb-1">Booking Link (optional)</label>
              <Input
                data-testid="gen-booking"
                value={bookingLink}
                onChange={(e) => setBookingLink(e.target.value)}
                placeholder="https://calendly.com/your-link"
                className="border-border rounded-none focus:border-[#002FA7] focus:ring-0"
              />
            </div>
            <button
              data-testid="generate-website-btn"
              type="submit"
              disabled={generating}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-[#002FA7] text-white text-sm font-semibold hover:bg-[#002482] transition-colors duration-100 disabled:opacity-50"
            >
              {generating ? (
                <>
                  <div className="spinner" style={{ borderTopColor: "white", borderColor: "rgba(255,255,255,0.3)" }} />
                  Generating (~20s)...
                </>
              ) : (
                <>
                  <Lightning weight="bold" size={16} />
                  Generate Website
                </>
              )}
            </button>
          </form>

          {generatedHtml && (
            <div className="space-y-2 pt-3 border-t border-border">
              <div className="flex gap-2">
                <button
                  data-testid="download-html-btn"
                  onClick={handleDownload}
                  className="flex-1 flex items-center justify-center gap-1 px-3 py-2 border border-border text-xs font-semibold hover:border-[#002FA7] hover:text-[#002FA7] transition-colors"
                >
                  <DownloadSimple weight="bold" size={14} />
                  Download
                </button>
                <button
                  data-testid="fullscreen-btn"
                  onClick={handleFullscreen}
                  className="flex-1 flex items-center justify-center gap-1 px-3 py-2 border border-border text-xs font-semibold hover:border-[#002FA7] hover:text-[#002FA7] transition-colors"
                >
                  <ArrowsOut weight="bold" size={14} />
                  Full View
                </button>
              </div>
              <button
                data-testid="toggle-code-btn"
                onClick={() => setShowCode(!showCode)}
                className="w-full flex items-center justify-center gap-1 px-3 py-2 border border-border text-xs font-semibold hover:border-[#002FA7] hover:text-[#002FA7] transition-colors"
              >
                <Code weight="bold" size={14} />
                {showCode ? "Hide Code" : "View Code"}
              </button>
            </div>
          )}

          {/* Photo Enhancer */}
          <div className="pt-4 border-t border-border space-y-3">
            <h3 className="text-sm font-bold tracking-tight" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
              <Sparkle weight="bold" size={14} className="inline mr-1 text-[#002FA7]" />
              AI Photo Enhancer
            </h3>
            <p className="text-[11px] text-[#52525B]">Paste a photo URL from any business site. AI recreates it as a professional image.</p>
            <Input
              data-testid="enhance-url"
              value={enhanceUrl}
              onChange={(e) => setEnhanceUrl(e.target.value)}
              placeholder="Paste image URL to enhance"
              className="border-border rounded-none focus:border-[#002FA7] focus:ring-0 text-xs"
            />
            <Select value={enhanceType} onValueChange={setEnhanceType}>
              <SelectTrigger data-testid="enhance-type" className="rounded-none border-border text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="professional">Professional</SelectItem>
                <SelectItem value="modern">Modern / Bold</SelectItem>
                <SelectItem value="warm">Warm / Inviting</SelectItem>
                <SelectItem value="clean">Clean / Minimal</SelectItem>
              </SelectContent>
            </Select>
            <button
              data-testid="enhance-photo-btn"
              onClick={async () => {
                if (!enhanceUrl.trim()) { toast.error("Paste an image URL"); return; }
                setEnhancing(true);
                setEnhancedImage(null);
                try {
                  const res = await axios.post(`${API}/photos/enhance`, {
                    image_url: enhanceUrl.trim(),
                    enhancement_type: enhanceType,
                    business_type: industry || "business",
                  });
                  setEnhancedImage(res.data.enhanced_base64);
                  toast.success("Photo enhanced!");
                } catch (e) {
                  toast.error("Enhancement failed. Try a different image URL.");
                } finally {
                  setEnhancing(false);
                }
              }}
              disabled={enhancing}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 border border-[#002FA7] text-[#002FA7] text-xs font-semibold hover:bg-[#002FA7] hover:text-white transition-colors disabled:opacity-50"
            >
              {enhancing ? (
                <>
                  <div className="spinner" style={{ width: 14, height: 14 }} />
                  Enhancing (~30s)...
                </>
              ) : (
                <>
                  <Sparkle weight="bold" size={14} />
                  Enhance Photo
                </>
              )}
            </button>
            {enhancedImage && (
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <p className="text-[10px] font-bold text-[#52525B] mb-1">Original</p>
                    <img src={enhanceUrl} alt="Original" className="w-full h-24 object-cover border border-border" />
                  </div>
                  <div>
                    <p className="text-[10px] font-bold text-[#00C853] mb-1">Enhanced</p>
                    <img src={`data:image/png;base64,${enhancedImage}`} alt="Enhanced" className="w-full h-24 object-cover border border-[#00C853]" />
                  </div>
                </div>
                <a
                  href={`data:image/png;base64,${enhancedImage}`}
                  download="enhanced_photo.png"
                  data-testid="download-enhanced"
                  className="w-full flex items-center justify-center gap-1 px-3 py-2 border border-border text-xs font-semibold hover:border-[#002FA7] hover:text-[#002FA7] transition-colors"
                >
                  <DownloadSimple weight="bold" size={12} />
                  Download Enhanced
                </a>
              </div>
            )}
          </div>
        </div>

        {/* Preview Panel */}
        <div className="lg:col-span-2 bg-white border border-border flex flex-col">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-[#FAFAFA]">
            <div className="flex items-center gap-2">
              <Eye weight="bold" size={14} className="text-[#52525B]" />
              <span className="text-xs font-bold uppercase tracking-[0.15em] text-[#52525B]">
                {showCode ? "Source Code" : "Live Preview"}
              </span>
            </div>
            {generatedHtml && (
              <span className="text-xs text-[#00C853] font-semibold">Ready</span>
            )}
          </div>
          <div className="flex-1 relative" style={{ minHeight: "500px" }}>
            {!generatedHtml && !generating && (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-[#A1A1AA]">
                <Globe weight="thin" size={64} className="mb-4" />
                <p className="text-sm font-medium">Configure and generate a website</p>
                <p className="text-xs mt-1">The preview will appear here</p>
              </div>
            )}
            {generating && (
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <div className="spinner mb-4" style={{ width: 32, height: 32 }} />
                <p className="text-sm font-medium text-[#0A0A0A]">Generating website with AI...</p>
                <p className="text-xs text-[#52525B] mt-1">This typically takes 15-30 seconds</p>
              </div>
            )}
            {generatedHtml && !showCode && (
              <iframe
                ref={iframeRef}
                data-testid="website-preview-iframe"
                srcDoc={generatedHtml}
                className="w-full h-full border-none"
                style={{ minHeight: "500px" }}
                title="Website Preview"
                sandbox="allow-scripts"
              />
            )}
            {generatedHtml && showCode && (
              <pre className="p-4 text-xs font-mono overflow-auto h-full bg-[#0A0A0A] text-[#E5E5E5]" style={{ minHeight: "500px" }}>
                {generatedHtml}
              </pre>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
